import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Mail, Check, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const SharePaymentLinkModal = ({ open, onClose, checkoutUrl, customerEmail, customerPhone, bookingDetails }) => {
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleCopy = () => {
    if (!checkoutUrl) return;
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    toast.success('Payment link copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handleEmailCustomer = async () => {
    if (!customerEmail) {
      toast.error('No customer email available');
      return;
    }
    
    if (!checkoutUrl) {
      toast.error('No payment link available');
      return;
    }
    
    try {
      setSendingEmail(true);
      
      // Send payment link email
      const response = await api.post('/admin/send-payment-link-email', {
        email: customerEmail,
        checkoutUrl: checkoutUrl,
        bookingDetails: {
          serviceName: bookingDetails?.serviceName || 'Your Booking',
          preferredDate: bookingDetails?.preferredDate,
          preferredTime: bookingDetails?.preferredTime,
          customerName: bookingDetails?.customerName,
          totalAmount: bookingDetails?.totalAmount,
          currency: bookingDetails?.currency || 'USD'
        }
      });
      
      if (response.data?.success) {
        toast.success(`Payment link sent to ${customerEmail}`);
      } else {
        throw new Error(response.data?.message || 'Failed to send');
      }
    } catch (error) {
      console.error('Email error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send email';
      console.error('Error message:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  // Format booking details for display
  const formatBookingDate = () => {
    if (!bookingDetails?.preferredDate) return 'N/A';
    try {
      const date = new Date(bookingDetails.preferredDate + 'T00:00:00');
      const dateStr = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric' 
      });
      
      // Format time
      if (bookingDetails.preferredTime) {
        const [hours, minutes] = bookingDetails.preferredTime.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'pm' : 'am';
        const hour12 = hour % 12 || 12;
        const timeStr = `${hour12}:${minutes}${ampm}`;
        return `${dateStr} at ${timeStr}`;
      }
      return dateStr;
    } catch {
      return bookingDetails.preferredDate;
    }
  };

  // Format currency amount
  const formatAmount = () => {
    const amount = parseFloat(bookingDetails?.totalAmount || 0);
    const currency = bookingDetails?.currency || bookingDetails?.presentmentCurrency || 'USD';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(amount);
    } catch {
      // Fallback for unknown currencies
      return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-3">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            Payment Link Created!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800">
              ✅ Share this payment link with your customer.
            </p>
          </div>

          {/* Payment Link Display */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
              Payment Link
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-600 font-mono overflow-hidden">
                <span className="block" title={checkoutUrl}>
                  {checkoutUrl 
                    ? checkoutUrl.length > 50 
                      ? `${checkoutUrl.slice(0, 25)}...${checkoutUrl.slice(-20)}`
                      : checkoutUrl
                    : 'Loading...'}
                </span>
              </div>
              <Button
                onClick={handleCopy}
                variant="outline"
                size="sm"
                className="flex-shrink-0 h-10 px-4 sm:w-auto w-full"
                disabled={!checkoutUrl}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1.5" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Send to Customer */}
          {customerEmail && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                Send to Customer
              </label>
              <Button
                onClick={handleEmailCustomer}
                disabled={sendingEmail || !customerEmail}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Email to {customerEmail}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Booking Details Summary */}
          {bookingDetails && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Booking Details</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 flex-shrink-0">Service:</span>
                  <span className="text-gray-900 font-medium text-right ml-4">{bookingDetails.serviceName || 'Custom Booking'}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 flex-shrink-0">Date:</span>
                  <span className="text-gray-900 text-right ml-4">{formatBookingDate()}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 flex-shrink-0">Customer:</span>
                  <span className="text-gray-900 text-right ml-4">{bookingDetails.customerName || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-start pt-2 border-t border-gray-200 mt-2">
                  <span className="text-gray-700 font-medium flex-shrink-0">Amount Due:</span>
                  <span className="text-green-600 font-bold text-lg text-right ml-4">{formatAmount()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3 border-t border-gray-200">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 h-10"
            >
              Done
            </Button>
            <Button
              onClick={() => window.open(checkoutUrl, '_blank')}
              disabled={!checkoutUrl}
              className="flex-1 bg-purple-600 hover:bg-purple-700 h-10"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePaymentLinkModal;
